import { RevEmptyState } from "@revmatics/catalyst";
import {
  ColumnDef,
  ColumnPinningState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  OnChangeFn,
  Row,
  useReactTable,
} from "@tanstack/react-table";
import { Fragment, ReactElement } from "react";

type Props<TData> = {
  data: TData[];
  columns: ColumnDef<TData>[];
  renderSubComponent: (props: {
    row: Row<TData>;
    edit: boolean;
  }) => ReactElement;
  getRowCanExpand: (row: Row<TData>) => boolean;
  columnPinning?: ColumnPinningState;
  onColumnPinningChange?: OnChangeFn<ColumnPinningState>;
  edit: boolean;
};
export function RevMediaPlanTable<TData extends object>({
  data,
  columns,
  renderSubComponent,
  getRowCanExpand,
  columnPinning,
  edit,
}: Props<TData>) {
  const table = useReactTable<TData>({
    data,
    columns,
    getRowCanExpand,
    state: {
      columnPinning: columnPinning ?? {},
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  return (
    <div className="overflow-x-auto scroll-horizontal rounded-lg overflow-hidden shadow-[0px_2px_4px_0px_rgb(0_0_0_/_0.15)]">
      <table className="min-w-full divide-y divide-monochrome-100 ">
        <thead className="bg-primary-614 divide-y divide-monochrome-200">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header, headerIndex, headersArray) => {
                const isPinned = header.column.getIsPinned();
                return (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    rowSpan={
                      headerIndex === 0 && header.id !== "expander"
                        ? 3
                        : undefined
                    }
                    className={`px-4 py-3 text-left typography-body-bold-sm text-monochrome-900 group ${headerIndex < headersArray.length - 1 && !isPinned ? "border-r border-monochrome-200" : ""}
                    ${headerIndex === 0 && header.id === "expander" ? "hidden" : ""}
                    ${isPinned ? "shadow-elevation-inset-right-200 sticky z-10 left-0 bg-primary-614" : ""}
                    `}
                  >
                    {header.isPlaceholder ? null : (
                      <div>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody className="bg-white divide-y divide-monochrome-100">
          {table.getRowModel().rows.map((row) => {
            return (
              <Fragment key={row.id}>
                <tr
                  className={`${row.getIsExpanded() ? "border-t-2 border-t-primary-200 border-x-2 border-x-primary-200 bg-primary-508" : ""}`}
                >
                  {row.getVisibleCells().map((cell, cellIndex, cellsArray) => {
                    const isPinned = cell.column.getIsPinned();
                    return (
                      <td
                        key={cell.id}
                        className={` px-4 py-3 whitespace-nowrap text-sm text-monochrome-900 
                        ${cellIndex < cellsArray.length - 1 && !isPinned ? "border-r border-monochrome-100" : ""}
                         ${isPinned ? `shadow-elevation-inset-right-100 sticky z-10 left-0 ${row.getIsExpanded() ? "bg-primary-508" : "bg-white"} ` : ""}`}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    );
                  })}
                </tr>
                {row.getIsExpanded() && (
                  <tr
                    className={`${row.getIsExpanded() ? "border-b-2 border-b-primary-200 border-x-2 border-x-primary-200 bg-primary-505" : ""}`}
                  >
                    <td colSpan={row.getVisibleCells().length} className="pb-4">
                      {renderSubComponent({ row, edit })}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
          {table.getRowModel().rows.length === 0 && (
            <tr>
              <td
                colSpan={columns.length}
                className="text-center py-10 text-monochrome-500"
              >
                <RevEmptyState />
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
